import { useNavigate } from "react-router-dom";
import { AlertTriangle, Plus, Truck, Users, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, JobTypeChip } from "@/components/common";
import { loadLevel } from "@/components/calendar/MonthGrid";
import { fmtDate, fmtTime, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const groupCount = (jobs, key) =>
  jobs.reduce((acc, j) => (j[key] ? { ...acc, [j[key]]: [...(acc[j[key]] || []), j] } : acc), {});

const clashes = (jobs, key) =>
  Object.entries(groupCount(jobs, key)).filter(([, list]) => {
    const times = list.map((j) => j.start_time).filter(Boolean);
    return new Set(times).size < times.length;
  }).map(([name]) => name);

const Resource = ({ icon: Icon, title, groups, conflicts }) => (
  <div>
    <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" />{title}</div>
    <div className="flex flex-wrap gap-1.5">
      {Object.keys(groups).length === 0 && <span className="text-xs text-slate-400">None assigned</span>}
      {Object.entries(groups).map(([name, list]) => (
        <span key={name} className={cn("rounded-md border px-2 py-1 text-xs", conflicts.includes(name) ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-slate-50 text-slate-700")}>
          {name} <span className="font-mono font-semibold">×{list.length}</span>
        </span>
      ))}
    </div>
  </div>
);

export const DayPanel = ({ date, jobs, capacity }) => {
  const navigate = useNavigate();
  const active = jobs.filter((j) => j.status !== "Cancelled");
  const cancelled = jobs.length - active.length;
  const lvl = loadLevel(active.length, capacity);
  const teamClash = clashes(active, "assigned_team");
  const truckClash = clashes(active, "truck_assigned");
  const men = active.reduce((s, j) => s + (Number(j.men) || 0), 0);
  const unassigned = active.filter((j) => !j.assigned_team).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="calendar-day-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 p-5">
        <div>
          <h2 className="font-display text-xl font-bold text-[#0E1E38]" data-testid="day-panel-date">{fmtDate(date, "EEEE d MMMM")}</h2>
          <p className="mt-1 text-sm text-slate-500">
            <span className="font-mono font-semibold text-slate-900" data-testid="day-panel-count">{active.length}</span> / {capacity} jobs · {men} men · {unassigned} unassigned{cancelled ? ` · ${cancelled} cancelled` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/run-sheet?date=${date}`)} data-testid="btn-run-sheet"><Printer className="h-4 w-4" /> Run sheets</Button>
          <Button size="sm" onClick={() => navigate(`/bookings/new?date=${date}`)} data-testid="btn-new-booking-for-day" className="bg-[#0E1E38] hover:bg-[#162A4D]"><Plus className="h-4 w-4" /> Book this day</Button>
        </div>
      </div>
      <div className="space-y-4 p-5">
        <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className={cn("h-full", lvl.bar)} style={{ width: `${Math.min((active.length / capacity) * 100, 100)}%` }} /></div>
        {(lvl.key === "over" || lvl.key === "full" || teamClash.length > 0 || truckClash.length > 0) && (
          <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="overbooking-alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              {lvl.key === "over" && <div className="font-semibold">Overbooked – {active.length} jobs against a capacity of {capacity}.</div>}
              {lvl.key === "full" && <div className="font-semibold">Near capacity – {capacity - active.length} job slots left.</div>}
              {teamClash.length > 0 && <div>Team double-booked at the same start time: {teamClash.join(", ")}</div>}
              {truckClash.length > 0 && <div>Truck double-booked at the same start time: {truckClash.join(", ")}</div>}
            </div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Resource icon={Users} title="Drivers / teams" groups={groupCount(active, "assigned_team")} conflicts={teamClash} />
          <Resource icon={Truck} title="Trucks" groups={groupCount(active, "truck_assigned")} conflicts={truckClash} />
        </div>
      </div>
      <div className="border-t border-slate-100">
        {jobs.length === 0 && <p className="p-5 text-sm text-slate-500" data-testid="day-panel-empty">No moves booked for this day.</p>}
        <ul className="divide-y divide-slate-100" data-testid="day-jobs-list">
          {jobs.map((j) => (
            <li key={j.id} onClick={() => navigate(`/bookings/${j.id}`)} data-testid={`day-job-${j.id}`} className={cn("grid cursor-pointer grid-cols-[64px_1fr] gap-3 px-5 py-3.5 hover:bg-sky-50/60", j.status === "Cancelled" && "opacity-50")}>
              <div className="font-mono text-sm font-bold text-[#0E1E38]">{fmtTime(j.start_time)}</div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold text-sky-700">{j.job_number}</span>
                  <span className="font-semibold text-slate-900">{j.customer_name}</span>
                  <JobTypeChip name={j.job_type} />
                  <StatusBadge status={j.status} className="ml-auto" />
                </div>
                <div className="mt-1 truncate text-sm text-slate-600">{j.pickup_address} → {j.delivery_address}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
                  <span className={cn(!j.assigned_team && "font-medium text-amber-600")}>Team: {j.assigned_team || "Unassigned"}</span>
                  <span>Truck: {j.truck_assigned || j.truck_size || "—"}</span>
                  {j.men && <span>{j.men} men</span>}
                  <span>Balance {money(j.remaining_balance)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
